/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `lap` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class LapMesg : Mesg {
    public constructor() : super("Lap", Profile.MesgNum.LAP)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** message_index. */
    public var messageIndex: UShort?
        get() = getFieldValue(MESSAGE_INDEX_FIELD_NUM) as? UShort
        set(value) = setFieldValue(MESSAGE_INDEX_FIELD_NUM, value)

    /** timestamp, in s. */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** event. */
    public var event: Event?
        get() = (getFieldValue(EVENT_FIELD_NUM) as? UByte)?.let { Event.fromValue(it) }
        set(value) = setFieldValue(EVENT_FIELD_NUM, value?.value)

    /** event_type. */
    public var eventType: EventType?
        get() = (getFieldValue(EVENT_TYPE_FIELD_NUM) as? UByte)?.let { EventType.fromValue(it) }
        set(value) = setFieldValue(EVENT_TYPE_FIELD_NUM, value?.value)

    /** start_time. */
    public var startTime: Instant?
        get() = (getFieldValue(START_TIME_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(START_TIME_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** start_position_lat, in semicircles. */
    public var startPositionLat: Int?
        get() = getFieldValue(START_POSITION_LAT_FIELD_NUM) as? Int
        set(value) = setFieldValue(START_POSITION_LAT_FIELD_NUM, value)

    /** start_position_long, in semicircles. */
    public var startPositionLong: Int?
        get() = getFieldValue(START_POSITION_LONG_FIELD_NUM) as? Int
        set(value) = setFieldValue(START_POSITION_LONG_FIELD_NUM, value)

    /** end_position_lat, in semicircles. */
    public var endPositionLat: Int?
        get() = getFieldValue(END_POSITION_LAT_FIELD_NUM) as? Int
        set(value) = setFieldValue(END_POSITION_LAT_FIELD_NUM, value)

    /** end_position_long, in semicircles. */
    public var endPositionLong: Int?
        get() = getFieldValue(END_POSITION_LONG_FIELD_NUM) as? Int
        set(value) = setFieldValue(END_POSITION_LONG_FIELD_NUM, value)

    /** total_elapsed_time, in s. Time (includes pauses) */
    public var totalElapsedTime: Double?
        get() = getFieldValue(TOTAL_ELAPSED_TIME_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_ELAPSED_TIME_FIELD_NUM, value)

    /** total_timer_time, in s. Timer Time (excludes pauses) */
    public var totalTimerTime: Double?
        get() = getFieldValue(TOTAL_TIMER_TIME_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_TIMER_TIME_FIELD_NUM, value)

    /** total_distance, in m. */
    public var totalDistance: Double?
        get() = getFieldValue(TOTAL_DISTANCE_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_DISTANCE_FIELD_NUM, value)

    /** total_cycles, in cycles. */
    public var totalCycles: UInt?
        get() = getFieldValue(TOTAL_CYCLES_FIELD_NUM) as? UInt
        set(value) = setFieldValue(TOTAL_CYCLES_FIELD_NUM, value)

    /**
     * total_strides: what total_cycles means when this message selects it.
     */
    public val totalStrides: UInt?
        get() = getFieldValue(TOTAL_CYCLES_FIELD_NUM, 0, SubFieldSelector.Named("TotalStrides")) as? UInt

    public fun setTotalStrides(value: UInt?) {
        setFieldValue(TOTAL_CYCLES_FIELD_NUM, value, 0, SubFieldSelector.Named("TotalStrides"))
    }

    /**
     * total_strokes: what total_cycles means when this message selects it.
     */
    public val totalStrokes: UInt?
        get() = getFieldValue(TOTAL_CYCLES_FIELD_NUM, 0, SubFieldSelector.Named("TotalStrokes")) as? UInt

    public fun setTotalStrokes(value: UInt?) {
        setFieldValue(TOTAL_CYCLES_FIELD_NUM, value, 0, SubFieldSelector.Named("TotalStrokes"))
    }

    /**
     * total_pushes: what total_cycles means when this message selects it.
     *
     * total number of pushes in a lap
     */
    public val totalPushes: UInt?
        get() = getFieldValue(TOTAL_CYCLES_FIELD_NUM, 0, SubFieldSelector.Named("TotalPushes")) as? UInt

    public fun setTotalPushes(value: UInt?) {
        setFieldValue(TOTAL_CYCLES_FIELD_NUM, value, 0, SubFieldSelector.Named("TotalPushes"))
    }

    /** total_calories, in kcal. */
    public var totalCalories: UShort?
        get() = getFieldValue(TOTAL_CALORIES_FIELD_NUM) as? UShort
        set(value) = setFieldValue(TOTAL_CALORIES_FIELD_NUM, value)

    /** total_fat_calories, in kcal. If New Leaf */
    public var totalFatCalories: UShort?
        get() = getFieldValue(TOTAL_FAT_CALORIES_FIELD_NUM) as? UShort
        set(value) = setFieldValue(TOTAL_FAT_CALORIES_FIELD_NUM, value)

    /** avg_speed, in m/s. */
    public var avgSpeed: Double?
        get() = getFieldValue(AVG_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_SPEED_FIELD_NUM, value)

    /** max_speed, in m/s. */
    public var maxSpeed: Double?
        get() = getFieldValue(MAX_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(MAX_SPEED_FIELD_NUM, value)

    /** avg_heart_rate, in bpm. */
    public var avgHeartRate: UByte?
        get() = getFieldValue(AVG_HEART_RATE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(AVG_HEART_RATE_FIELD_NUM, value)

    /** max_heart_rate, in bpm. */
    public var maxHeartRate: UByte?
        get() = getFieldValue(MAX_HEART_RATE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(MAX_HEART_RATE_FIELD_NUM, value)

    /** avg_cadence, in rpm. total_cycles / total_timer_time if non_zero_avg_cadence otherwise total_cycles / total_elapsed_time */
    public var avgCadence: UByte?
        get() = getFieldValue(AVG_CADENCE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(AVG_CADENCE_FIELD_NUM, value)

    /**
     * avg_running_cadence: what avg_cadence means when this message selects it.
     */
    public val avgRunningCadence: UByte?
        get() = getFieldValue(AVG_CADENCE_FIELD_NUM, 0, SubFieldSelector.Named("AvgRunningCadence")) as? UByte

    public fun setAvgRunningCadence(value: UByte?) {
        setFieldValue(AVG_CADENCE_FIELD_NUM, value, 0, SubFieldSelector.Named("AvgRunningCadence"))
    }

    /**
     * avg_swimming_cadence: what avg_cadence means when this message selects it.
     */
    public val avgSwimmingCadence: UByte?
        get() = getFieldValue(AVG_CADENCE_FIELD_NUM, 0, SubFieldSelector.Named("AvgSwimmingCadence")) as? UByte

    public fun setAvgSwimmingCadence(value: UByte?) {
        setFieldValue(AVG_CADENCE_FIELD_NUM, value, 0, SubFieldSelector.Named("AvgSwimmingCadence"))
    }

    /**
     * avg_paddlesport_cadence: what avg_cadence means when this message selects it.
     */
    public val avgPaddlesportCadence: UByte?
        get() = getFieldValue(AVG_CADENCE_FIELD_NUM, 0, SubFieldSelector.Named("AvgPaddlesportCadence")) as? UByte

    public fun setAvgPaddlesportCadence(value: UByte?) {
        setFieldValue(AVG_CADENCE_FIELD_NUM, value, 0, SubFieldSelector.Named("AvgPaddlesportCadence"))
    }

    /**
     * avg_push_cadence: what avg_cadence means when this message selects it.
     *
     * average pushes per minute in a lap
     */
    public val avgPushCadence: UByte?
        get() = getFieldValue(AVG_CADENCE_FIELD_NUM, 0, SubFieldSelector.Named("AvgPushCadence")) as? UByte

    public fun setAvgPushCadence(value: UByte?) {
        setFieldValue(AVG_CADENCE_FIELD_NUM, value, 0, SubFieldSelector.Named("AvgPushCadence"))
    }

    /** max_cadence, in rpm. */
    public var maxCadence: UByte?
        get() = getFieldValue(MAX_CADENCE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(MAX_CADENCE_FIELD_NUM, value)

    /**
     * max_running_cadence: what max_cadence means when this message selects it.
     */
    public val maxRunningCadence: UByte?
        get() = getFieldValue(MAX_CADENCE_FIELD_NUM, 0, SubFieldSelector.Named("MaxRunningCadence")) as? UByte

    public fun setMaxRunningCadence(value: UByte?) {
        setFieldValue(MAX_CADENCE_FIELD_NUM, value, 0, SubFieldSelector.Named("MaxRunningCadence"))
    }

    /**
     * max_swimming_cadence: what max_cadence means when this message selects it.
     */
    public val maxSwimmingCadence: UByte?
        get() = getFieldValue(MAX_CADENCE_FIELD_NUM, 0, SubFieldSelector.Named("MaxSwimmingCadence")) as? UByte

    public fun setMaxSwimmingCadence(value: UByte?) {
        setFieldValue(MAX_CADENCE_FIELD_NUM, value, 0, SubFieldSelector.Named("MaxSwimmingCadence"))
    }

    /**
     * max_paddlesport_cadence: what max_cadence means when this message selects it.
     */
    public val maxPaddlesportCadence: UByte?
        get() = getFieldValue(MAX_CADENCE_FIELD_NUM, 0, SubFieldSelector.Named("MaxPaddlesportCadence")) as? UByte

    public fun setMaxPaddlesportCadence(value: UByte?) {
        setFieldValue(MAX_CADENCE_FIELD_NUM, value, 0, SubFieldSelector.Named("MaxPaddlesportCadence"))
    }

    /**
     * max_push_cadence: what max_cadence means when this message selects it.
     *
     * max pushes per minute in a lap
     */
    public val maxPushCadence: UByte?
        get() = getFieldValue(MAX_CADENCE_FIELD_NUM, 0, SubFieldSelector.Named("MaxPushCadence")) as? UByte

    public fun setMaxPushCadence(value: UByte?) {
        setFieldValue(MAX_CADENCE_FIELD_NUM, value, 0, SubFieldSelector.Named("MaxPushCadence"))
    }

    /** avg_power, in watts. total_power / total_timer_time if non_zero_avg_power otherwise total_power / total_elapsed_time */
    public var avgPower: UShort?
        get() = getFieldValue(AVG_POWER_FIELD_NUM) as? UShort
        set(value) = setFieldValue(AVG_POWER_FIELD_NUM, value)

    /** max_power, in watts. */
    public var maxPower: UShort?
        get() = getFieldValue(MAX_POWER_FIELD_NUM) as? UShort
        set(value) = setFieldValue(MAX_POWER_FIELD_NUM, value)

    /** total_ascent, in m. */
    public var totalAscent: UShort?
        get() = getFieldValue(TOTAL_ASCENT_FIELD_NUM) as? UShort
        set(value) = setFieldValue(TOTAL_ASCENT_FIELD_NUM, value)

    /** total_descent, in m. */
    public var totalDescent: UShort?
        get() = getFieldValue(TOTAL_DESCENT_FIELD_NUM) as? UShort
        set(value) = setFieldValue(TOTAL_DESCENT_FIELD_NUM, value)

    /** intensity. */
    public var intensity: Intensity?
        get() = (getFieldValue(INTENSITY_FIELD_NUM) as? UByte)?.let { Intensity.fromValue(it) }
        set(value) = setFieldValue(INTENSITY_FIELD_NUM, value?.value)

    /** lap_trigger. */
    public var lapTrigger: LapTrigger?
        get() = (getFieldValue(LAP_TRIGGER_FIELD_NUM) as? UByte)?.let { LapTrigger.fromValue(it) }
        set(value) = setFieldValue(LAP_TRIGGER_FIELD_NUM, value?.value)

    /** sport. */
    public var sport: Sport?
        get() = (getFieldValue(SPORT_FIELD_NUM) as? UByte)?.let { Sport.fromValue(it) }
        set(value) = setFieldValue(SPORT_FIELD_NUM, value?.value)

    /** event_group. */
    public var eventGroup: UByte?
        get() = getFieldValue(EVENT_GROUP_FIELD_NUM) as? UByte
        set(value) = setFieldValue(EVENT_GROUP_FIELD_NUM, value)

    /** nec_lat, in semicircles. North east corner latitude. */
    public var necLat: Int?
        get() = getFieldValue(NEC_LAT_FIELD_NUM) as? Int
        set(value) = setFieldValue(NEC_LAT_FIELD_NUM, value)

    /** nec_long, in semicircles. North east corner longitude. */
    public var necLong: Int?
        get() = getFieldValue(NEC_LONG_FIELD_NUM) as? Int
        set(value) = setFieldValue(NEC_LONG_FIELD_NUM, value)

    /** swc_lat, in semicircles. South west corner latitude. */
    public var swcLat: Int?
        get() = getFieldValue(SWC_LAT_FIELD_NUM) as? Int
        set(value) = setFieldValue(SWC_LAT_FIELD_NUM, value)

    /** swc_long, in semicircles. South west corner latitude. */
    public var swcLong: Int?
        get() = getFieldValue(SWC_LONG_FIELD_NUM) as? Int
        set(value) = setFieldValue(SWC_LONG_FIELD_NUM, value)

    /** num_lengths, in lengths. # of lengths of swim pool */
    public var numLengths: UShort?
        get() = getFieldValue(NUM_LENGTHS_FIELD_NUM) as? UShort
        set(value) = setFieldValue(NUM_LENGTHS_FIELD_NUM, value)

    /** normalized_power, in watts. */
    public var normalizedPower: UShort?
        get() = getFieldValue(NORMALIZED_POWER_FIELD_NUM) as? UShort
        set(value) = setFieldValue(NORMALIZED_POWER_FIELD_NUM, value)

    /** left_right_balance. */
    public var leftRightBalance: UShort?
        get() = getFieldValue(LEFT_RIGHT_BALANCE_FIELD_NUM) as? UShort
        set(value) = setFieldValue(LEFT_RIGHT_BALANCE_FIELD_NUM, value)

    /** first_length_index. */
    public var firstLengthIndex: UShort?
        get() = getFieldValue(FIRST_LENGTH_INDEX_FIELD_NUM) as? UShort
        set(value) = setFieldValue(FIRST_LENGTH_INDEX_FIELD_NUM, value)

    /** avg_stroke_distance, in m. */
    public var avgStrokeDistance: Double?
        get() = getFieldValue(AVG_STROKE_DISTANCE_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_STROKE_DISTANCE_FIELD_NUM, value)

    /** swim_stroke. */
    public var swimStroke: SwimStroke?
        get() = (getFieldValue(SWIM_STROKE_FIELD_NUM) as? UByte)?.let { SwimStroke.fromValue(it) }
        set(value) = setFieldValue(SWIM_STROKE_FIELD_NUM, value?.value)

    /** sub_sport. */
    public var subSport: SubSport?
        get() = (getFieldValue(SUB_SPORT_FIELD_NUM) as? UByte)?.let { SubSport.fromValue(it) }
        set(value) = setFieldValue(SUB_SPORT_FIELD_NUM, value?.value)

    /** num_active_lengths, in lengths. # of active lengths of swim pool */
    public var numActiveLengths: UShort?
        get() = getFieldValue(NUM_ACTIVE_LENGTHS_FIELD_NUM) as? UShort
        set(value) = setFieldValue(NUM_ACTIVE_LENGTHS_FIELD_NUM, value)

    /** total_work, in J. */
    public var totalWork: UInt?
        get() = getFieldValue(TOTAL_WORK_FIELD_NUM) as? UInt
        set(value) = setFieldValue(TOTAL_WORK_FIELD_NUM, value)

    /** avg_altitude, in m. */
    public var avgAltitude: Double?
        get() = getFieldValue(AVG_ALTITUDE_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_ALTITUDE_FIELD_NUM, value)

    /** max_altitude, in m. */
    public var maxAltitude: Double?
        get() = getFieldValue(MAX_ALTITUDE_FIELD_NUM) as? Double
        set(value) = setFieldValue(MAX_ALTITUDE_FIELD_NUM, value)

    /** gps_accuracy, in m. */
    public var gpsAccuracy: UByte?
        get() = getFieldValue(GPS_ACCURACY_FIELD_NUM) as? UByte
        set(value) = setFieldValue(GPS_ACCURACY_FIELD_NUM, value)

    /** avg_grade, in %. */
    public var avgGrade: Double?
        get() = getFieldValue(AVG_GRADE_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_GRADE_FIELD_NUM, value)

    /** avg_pos_grade, in %. */
    public var avgPosGrade: Double?
        get() = getFieldValue(AVG_POS_GRADE_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_POS_GRADE_FIELD_NUM, value)

    /** avg_neg_grade, in %. */
    public var avgNegGrade: Double?
        get() = getFieldValue(AVG_NEG_GRADE_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_NEG_GRADE_FIELD_NUM, value)

    /** max_pos_grade, in %. */
    public var maxPosGrade: Double?
        get() = getFieldValue(MAX_POS_GRADE_FIELD_NUM) as? Double
        set(value) = setFieldValue(MAX_POS_GRADE_FIELD_NUM, value)

    /** max_neg_grade, in %. */
    public var maxNegGrade: Double?
        get() = getFieldValue(MAX_NEG_GRADE_FIELD_NUM) as? Double
        set(value) = setFieldValue(MAX_NEG_GRADE_FIELD_NUM, value)

    /** avg_temperature, in C. */
    public var avgTemperature: Byte?
        get() = getFieldValue(AVG_TEMPERATURE_FIELD_NUM) as? Byte
        set(value) = setFieldValue(AVG_TEMPERATURE_FIELD_NUM, value)

    /** max_temperature, in C. */
    public var maxTemperature: Byte?
        get() = getFieldValue(MAX_TEMPERATURE_FIELD_NUM) as? Byte
        set(value) = setFieldValue(MAX_TEMPERATURE_FIELD_NUM, value)

    /** total_moving_time, in s. */
    public var totalMovingTime: Double?
        get() = getFieldValue(TOTAL_MOVING_TIME_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_MOVING_TIME_FIELD_NUM, value)

    /** avg_pos_vertical_speed, in m/s. */
    public var avgPosVerticalSpeed: Double?
        get() = getFieldValue(AVG_POS_VERTICAL_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_POS_VERTICAL_SPEED_FIELD_NUM, value)

    /** avg_neg_vertical_speed, in m/s. */
    public var avgNegVerticalSpeed: Double?
        get() = getFieldValue(AVG_NEG_VERTICAL_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_NEG_VERTICAL_SPEED_FIELD_NUM, value)

    /** max_pos_vertical_speed, in m/s. */
    public var maxPosVerticalSpeed: Double?
        get() = getFieldValue(MAX_POS_VERTICAL_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(MAX_POS_VERTICAL_SPEED_FIELD_NUM, value)

    /** max_neg_vertical_speed, in m/s. */
    public var maxNegVerticalSpeed: Double?
        get() = getFieldValue(MAX_NEG_VERTICAL_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(MAX_NEG_VERTICAL_SPEED_FIELD_NUM, value)

    /** Number of values stored in time_in_hr_zone. */
    public val numTimeInHrZone: Int
        get() = getField(TIME_IN_HR_ZONE_FIELD_NUM)?.numValues ?: 0

    /** time_in_hr_zone, in s. */
    public var timeInHrZone: List<Double?>?
        get() {
            val field = getField(TIME_IN_HR_ZONE_FIELD_NUM) ?: return null
            return List(field.numValues) { getTimeInHrZone(it) }
        }
        set(value) {
            removeField(TIME_IN_HR_ZONE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setTimeInHrZone(index, element) }
        }

    public fun getTimeInHrZone(index: Int): Double? =
        getFieldValue(TIME_IN_HR_ZONE_FIELD_NUM, index) as? Double

    public fun setTimeInHrZone(index: Int, value: Double?) {
        setFieldValue(TIME_IN_HR_ZONE_FIELD_NUM, value, index)
    }

    /** Number of values stored in time_in_speed_zone. */
    public val numTimeInSpeedZone: Int
        get() = getField(TIME_IN_SPEED_ZONE_FIELD_NUM)?.numValues ?: 0

    /** time_in_speed_zone, in s. */
    public var timeInSpeedZone: List<Double?>?
        get() {
            val field = getField(TIME_IN_SPEED_ZONE_FIELD_NUM) ?: return null
            return List(field.numValues) { getTimeInSpeedZone(it) }
        }
        set(value) {
            removeField(TIME_IN_SPEED_ZONE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setTimeInSpeedZone(index, element) }
        }

    public fun getTimeInSpeedZone(index: Int): Double? =
        getFieldValue(TIME_IN_SPEED_ZONE_FIELD_NUM, index) as? Double

    public fun setTimeInSpeedZone(index: Int, value: Double?) {
        setFieldValue(TIME_IN_SPEED_ZONE_FIELD_NUM, value, index)
    }

    /** Number of values stored in time_in_cadence_zone. */
    public val numTimeInCadenceZone: Int
        get() = getField(TIME_IN_CADENCE_ZONE_FIELD_NUM)?.numValues ?: 0

    /** time_in_cadence_zone, in s. */
    public var timeInCadenceZone: List<Double?>?
        get() {
            val field = getField(TIME_IN_CADENCE_ZONE_FIELD_NUM) ?: return null
            return List(field.numValues) { getTimeInCadenceZone(it) }
        }
        set(value) {
            removeField(TIME_IN_CADENCE_ZONE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setTimeInCadenceZone(index, element) }
        }

    public fun getTimeInCadenceZone(index: Int): Double? =
        getFieldValue(TIME_IN_CADENCE_ZONE_FIELD_NUM, index) as? Double

    public fun setTimeInCadenceZone(index: Int, value: Double?) {
        setFieldValue(TIME_IN_CADENCE_ZONE_FIELD_NUM, value, index)
    }

    /** Number of values stored in time_in_power_zone. */
    public val numTimeInPowerZone: Int
        get() = getField(TIME_IN_POWER_ZONE_FIELD_NUM)?.numValues ?: 0

    /** time_in_power_zone, in s. */
    public var timeInPowerZone: List<Double?>?
        get() {
            val field = getField(TIME_IN_POWER_ZONE_FIELD_NUM) ?: return null
            return List(field.numValues) { getTimeInPowerZone(it) }
        }
        set(value) {
            removeField(TIME_IN_POWER_ZONE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setTimeInPowerZone(index, element) }
        }

    public fun getTimeInPowerZone(index: Int): Double? =
        getFieldValue(TIME_IN_POWER_ZONE_FIELD_NUM, index) as? Double

    public fun setTimeInPowerZone(index: Int, value: Double?) {
        setFieldValue(TIME_IN_POWER_ZONE_FIELD_NUM, value, index)
    }

    /** repetition_num. */
    public var repetitionNum: UShort?
        get() = getFieldValue(REPETITION_NUM_FIELD_NUM) as? UShort
        set(value) = setFieldValue(REPETITION_NUM_FIELD_NUM, value)

    /** min_altitude, in m. */
    public var minAltitude: Double?
        get() = getFieldValue(MIN_ALTITUDE_FIELD_NUM) as? Double
        set(value) = setFieldValue(MIN_ALTITUDE_FIELD_NUM, value)

    /** min_heart_rate, in bpm. */
    public var minHeartRate: UByte?
        get() = getFieldValue(MIN_HEART_RATE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(MIN_HEART_RATE_FIELD_NUM, value)

    /** active_time, in s. */
    public var activeTime: Double?
        get() = getFieldValue(ACTIVE_TIME_FIELD_NUM) as? Double
        set(value) = setFieldValue(ACTIVE_TIME_FIELD_NUM, value)

    /** wkt_step_index. */
    public var wktStepIndex: UShort?
        get() = getFieldValue(WKT_STEP_INDEX_FIELD_NUM) as? UShort
        set(value) = setFieldValue(WKT_STEP_INDEX_FIELD_NUM, value)

    /** avg_swolf. */
    public var avgSwolf: UShort?
        get() = getFieldValue(AVG_SWOLF_FIELD_NUM) as? UShort
        set(value) = setFieldValue(AVG_SWOLF_FIELD_NUM, value)

    /** opponent_score. */
    public var opponentScore: UShort?
        get() = getFieldValue(OPPONENT_SCORE_FIELD_NUM) as? UShort
        set(value) = setFieldValue(OPPONENT_SCORE_FIELD_NUM, value)

    /** Number of values stored in stroke_count. */
    public val numStrokeCount: Int
        get() = getField(STROKE_COUNT_FIELD_NUM)?.numValues ?: 0

    /** stroke_count, in counts. stroke_type enum used as the index */
    public var strokeCount: List<UShort?>?
        get() {
            val field = getField(STROKE_COUNT_FIELD_NUM) ?: return null
            return List(field.numValues) { getStrokeCount(it) }
        }
        set(value) {
            removeField(STROKE_COUNT_FIELD_NUM)
            value?.forEachIndexed { index, element -> setStrokeCount(index, element) }
        }

    public fun getStrokeCount(index: Int): UShort? =
        getFieldValue(STROKE_COUNT_FIELD_NUM, index) as? UShort

    public fun setStrokeCount(index: Int, value: UShort?) {
        setFieldValue(STROKE_COUNT_FIELD_NUM, value, index)
    }

    /** Number of values stored in zone_count. */
    public val numZoneCount: Int
        get() = getField(ZONE_COUNT_FIELD_NUM)?.numValues ?: 0

    /** zone_count, in counts. zone number used as the index */
    public var zoneCount: List<UShort?>?
        get() {
            val field = getField(ZONE_COUNT_FIELD_NUM) ?: return null
            return List(field.numValues) { getZoneCount(it) }
        }
        set(value) {
            removeField(ZONE_COUNT_FIELD_NUM)
            value?.forEachIndexed { index, element -> setZoneCount(index, element) }
        }

    public fun getZoneCount(index: Int): UShort? =
        getFieldValue(ZONE_COUNT_FIELD_NUM, index) as? UShort

    public fun setZoneCount(index: Int, value: UShort?) {
        setFieldValue(ZONE_COUNT_FIELD_NUM, value, index)
    }

    /** avg_vertical_oscillation, in mm. */
    public var avgVerticalOscillation: Double?
        get() = getFieldValue(AVG_VERTICAL_OSCILLATION_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_VERTICAL_OSCILLATION_FIELD_NUM, value)

    /** avg_stance_time_percent, in percent. */
    public var avgStanceTimePercent: Double?
        get() = getFieldValue(AVG_STANCE_TIME_PERCENT_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_STANCE_TIME_PERCENT_FIELD_NUM, value)

    /** avg_stance_time, in ms. */
    public var avgStanceTime: Double?
        get() = getFieldValue(AVG_STANCE_TIME_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_STANCE_TIME_FIELD_NUM, value)

    /** avg_fractional_cadence, in rpm. fractional part of the avg_cadence */
    public var avgFractionalCadence: Double?
        get() = getFieldValue(AVG_FRACTIONAL_CADENCE_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_FRACTIONAL_CADENCE_FIELD_NUM, value)

    /** max_fractional_cadence, in rpm. fractional part of the max_cadence */
    public var maxFractionalCadence: Double?
        get() = getFieldValue(MAX_FRACTIONAL_CADENCE_FIELD_NUM) as? Double
        set(value) = setFieldValue(MAX_FRACTIONAL_CADENCE_FIELD_NUM, value)

    /** total_fractional_cycles, in cycles. fractional part of the total_cycles */
    public var totalFractionalCycles: Double?
        get() = getFieldValue(TOTAL_FRACTIONAL_CYCLES_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_FRACTIONAL_CYCLES_FIELD_NUM, value)

    /** player_score. */
    public var playerScore: UShort?
        get() = getFieldValue(PLAYER_SCORE_FIELD_NUM) as? UShort
        set(value) = setFieldValue(PLAYER_SCORE_FIELD_NUM, value)

    /** Number of values stored in avg_total_hemoglobin_conc. */
    public val numAvgTotalHemoglobinConc: Int
        get() = getField(AVG_TOTAL_HEMOGLOBIN_CONC_FIELD_NUM)?.numValues ?: 0

    /** avg_total_hemoglobin_conc, in g/dL. Avg saturated and unsaturated hemoglobin */
    public var avgTotalHemoglobinConc: List<Double?>?
        get() {
            val field = getField(AVG_TOTAL_HEMOGLOBIN_CONC_FIELD_NUM) ?: return null
            return List(field.numValues) { getAvgTotalHemoglobinConc(it) }
        }
        set(value) {
            removeField(AVG_TOTAL_HEMOGLOBIN_CONC_FIELD_NUM)
            value?.forEachIndexed { index, element -> setAvgTotalHemoglobinConc(index, element) }
        }

    public fun getAvgTotalHemoglobinConc(index: Int): Double? =
        getFieldValue(AVG_TOTAL_HEMOGLOBIN_CONC_FIELD_NUM, index) as? Double

    public fun setAvgTotalHemoglobinConc(index: Int, value: Double?) {
        setFieldValue(AVG_TOTAL_HEMOGLOBIN_CONC_FIELD_NUM, value, index)
    }

    /** Number of values stored in min_total_hemoglobin_conc. */
    public val numMinTotalHemoglobinConc: Int
        get() = getField(MIN_TOTAL_HEMOGLOBIN_CONC_FIELD_NUM)?.numValues ?: 0

    /** min_total_hemoglobin_conc, in g/dL. Min saturated and unsaturated hemoglobin */
    public var minTotalHemoglobinConc: List<Double?>?
        get() {
            val field = getField(MIN_TOTAL_HEMOGLOBIN_CONC_FIELD_NUM) ?: return null
            return List(field.numValues) { getMinTotalHemoglobinConc(it) }
        }
        set(value) {
            removeField(MIN_TOTAL_HEMOGLOBIN_CONC_FIELD_NUM)
            value?.forEachIndexed { index, element -> setMinTotalHemoglobinConc(index, element) }
        }

    public fun getMinTotalHemoglobinConc(index: Int): Double? =
        getFieldValue(MIN_TOTAL_HEMOGLOBIN_CONC_FIELD_NUM, index) as? Double

    public fun setMinTotalHemoglobinConc(index: Int, value: Double?) {
        setFieldValue(MIN_TOTAL_HEMOGLOBIN_CONC_FIELD_NUM, value, index)
    }

    /** Number of values stored in max_total_hemoglobin_conc. */
    public val numMaxTotalHemoglobinConc: Int
        get() = getField(MAX_TOTAL_HEMOGLOBIN_CONC_FIELD_NUM)?.numValues ?: 0

    /** max_total_hemoglobin_conc, in g/dL. Max saturated and unsaturated hemoglobin */
    public var maxTotalHemoglobinConc: List<Double?>?
        get() {
            val field = getField(MAX_TOTAL_HEMOGLOBIN_CONC_FIELD_NUM) ?: return null
            return List(field.numValues) { getMaxTotalHemoglobinConc(it) }
        }
        set(value) {
            removeField(MAX_TOTAL_HEMOGLOBIN_CONC_FIELD_NUM)
            value?.forEachIndexed { index, element -> setMaxTotalHemoglobinConc(index, element) }
        }

    public fun getMaxTotalHemoglobinConc(index: Int): Double? =
        getFieldValue(MAX_TOTAL_HEMOGLOBIN_CONC_FIELD_NUM, index) as? Double

    public fun setMaxTotalHemoglobinConc(index: Int, value: Double?) {
        setFieldValue(MAX_TOTAL_HEMOGLOBIN_CONC_FIELD_NUM, value, index)
    }

    /** Number of values stored in avg_saturated_hemoglobin_percent. */
    public val numAvgSaturatedHemoglobinPercent: Int
        get() = getField(AVG_SATURATED_HEMOGLOBIN_PERCENT_FIELD_NUM)?.numValues ?: 0

    /** avg_saturated_hemoglobin_percent, in %. Avg percentage of hemoglobin saturated with oxygen */
    public var avgSaturatedHemoglobinPercent: List<Double?>?
        get() {
            val field = getField(AVG_SATURATED_HEMOGLOBIN_PERCENT_FIELD_NUM) ?: return null
            return List(field.numValues) { getAvgSaturatedHemoglobinPercent(it) }
        }
        set(value) {
            removeField(AVG_SATURATED_HEMOGLOBIN_PERCENT_FIELD_NUM)
            value?.forEachIndexed { index, element -> setAvgSaturatedHemoglobinPercent(index, element) }
        }

    public fun getAvgSaturatedHemoglobinPercent(index: Int): Double? =
        getFieldValue(AVG_SATURATED_HEMOGLOBIN_PERCENT_FIELD_NUM, index) as? Double

    public fun setAvgSaturatedHemoglobinPercent(index: Int, value: Double?) {
        setFieldValue(AVG_SATURATED_HEMOGLOBIN_PERCENT_FIELD_NUM, value, index)
    }

    /** Number of values stored in min_saturated_hemoglobin_percent. */
    public val numMinSaturatedHemoglobinPercent: Int
        get() = getField(MIN_SATURATED_HEMOGLOBIN_PERCENT_FIELD_NUM)?.numValues ?: 0

    /** min_saturated_hemoglobin_percent, in %. Min percentage of hemoglobin saturated with oxygen */
    public var minSaturatedHemoglobinPercent: List<Double?>?
        get() {
            val field = getField(MIN_SATURATED_HEMOGLOBIN_PERCENT_FIELD_NUM) ?: return null
            return List(field.numValues) { getMinSaturatedHemoglobinPercent(it) }
        }
        set(value) {
            removeField(MIN_SATURATED_HEMOGLOBIN_PERCENT_FIELD_NUM)
            value?.forEachIndexed { index, element -> setMinSaturatedHemoglobinPercent(index, element) }
        }

    public fun getMinSaturatedHemoglobinPercent(index: Int): Double? =
        getFieldValue(MIN_SATURATED_HEMOGLOBIN_PERCENT_FIELD_NUM, index) as? Double

    public fun setMinSaturatedHemoglobinPercent(index: Int, value: Double?) {
        setFieldValue(MIN_SATURATED_HEMOGLOBIN_PERCENT_FIELD_NUM, value, index)
    }

    /** Number of values stored in max_saturated_hemoglobin_percent. */
    public val numMaxSaturatedHemoglobinPercent: Int
        get() = getField(MAX_SATURATED_HEMOGLOBIN_PERCENT_FIELD_NUM)?.numValues ?: 0

    /** max_saturated_hemoglobin_percent, in %. Max percentage of hemoglobin saturated with oxygen */
    public var maxSaturatedHemoglobinPercent: List<Double?>?
        get() {
            val field = getField(MAX_SATURATED_HEMOGLOBIN_PERCENT_FIELD_NUM) ?: return null
            return List(field.numValues) { getMaxSaturatedHemoglobinPercent(it) }
        }
        set(value) {
            removeField(MAX_SATURATED_HEMOGLOBIN_PERCENT_FIELD_NUM)
            value?.forEachIndexed { index, element -> setMaxSaturatedHemoglobinPercent(index, element) }
        }

    public fun getMaxSaturatedHemoglobinPercent(index: Int): Double? =
        getFieldValue(MAX_SATURATED_HEMOGLOBIN_PERCENT_FIELD_NUM, index) as? Double

    public fun setMaxSaturatedHemoglobinPercent(index: Int, value: Double?) {
        setFieldValue(MAX_SATURATED_HEMOGLOBIN_PERCENT_FIELD_NUM, value, index)
    }

    /** avg_strokes_per_length, in strokes / length. */
    public var avgStrokesPerLength: Double?
        get() = getFieldValue(AVG_STROKES_PER_LENGTH_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_STROKES_PER_LENGTH_FIELD_NUM, value)

    /** avg_left_torque_effectiveness, in percent. */
    public var avgLeftTorqueEffectiveness: Double?
        get() = getFieldValue(AVG_LEFT_TORQUE_EFFECTIVENESS_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_LEFT_TORQUE_EFFECTIVENESS_FIELD_NUM, value)

    /** avg_right_torque_effectiveness, in percent. */
    public var avgRightTorqueEffectiveness: Double?
        get() = getFieldValue(AVG_RIGHT_TORQUE_EFFECTIVENESS_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_RIGHT_TORQUE_EFFECTIVENESS_FIELD_NUM, value)

    /** avg_left_pedal_smoothness, in percent. */
    public var avgLeftPedalSmoothness: Double?
        get() = getFieldValue(AVG_LEFT_PEDAL_SMOOTHNESS_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_LEFT_PEDAL_SMOOTHNESS_FIELD_NUM, value)

    /** avg_right_pedal_smoothness, in percent. */
    public var avgRightPedalSmoothness: Double?
        get() = getFieldValue(AVG_RIGHT_PEDAL_SMOOTHNESS_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_RIGHT_PEDAL_SMOOTHNESS_FIELD_NUM, value)

    /** avg_combined_pedal_smoothness, in percent. */
    public var avgCombinedPedalSmoothness: Double?
        get() = getFieldValue(AVG_COMBINED_PEDAL_SMOOTHNESS_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_COMBINED_PEDAL_SMOOTHNESS_FIELD_NUM, value)

    /** front_gear_shift_count. */
    public var frontGearShiftCount: UShort?
        get() = getFieldValue(FRONT_GEAR_SHIFT_COUNT_FIELD_NUM) as? UShort
        set(value) = setFieldValue(FRONT_GEAR_SHIFT_COUNT_FIELD_NUM, value)

    /** rear_gear_shift_count. */
    public var rearGearShiftCount: UShort?
        get() = getFieldValue(REAR_GEAR_SHIFT_COUNT_FIELD_NUM) as? UShort
        set(value) = setFieldValue(REAR_GEAR_SHIFT_COUNT_FIELD_NUM, value)

    /** time_standing, in s. Total time spent in the standing position */
    public var timeStanding: Double?
        get() = getFieldValue(TIME_STANDING_FIELD_NUM) as? Double
        set(value) = setFieldValue(TIME_STANDING_FIELD_NUM, value)

    /** stand_count. Number of transitions to the standing state */
    public var standCount: UShort?
        get() = getFieldValue(STAND_COUNT_FIELD_NUM) as? UShort
        set(value) = setFieldValue(STAND_COUNT_FIELD_NUM, value)

    /** avg_left_pco, in mm. Average left platform center offset */
    public var avgLeftPco: Byte?
        get() = getFieldValue(AVG_LEFT_PCO_FIELD_NUM) as? Byte
        set(value) = setFieldValue(AVG_LEFT_PCO_FIELD_NUM, value)

    /** avg_right_pco, in mm. Average right platform center offset */
    public var avgRightPco: Byte?
        get() = getFieldValue(AVG_RIGHT_PCO_FIELD_NUM) as? Byte
        set(value) = setFieldValue(AVG_RIGHT_PCO_FIELD_NUM, value)

    /** Number of values stored in avg_left_power_phase. */
    public val numAvgLeftPowerPhase: Int
        get() = getField(AVG_LEFT_POWER_PHASE_FIELD_NUM)?.numValues ?: 0

    /** avg_left_power_phase, in degrees. Average left power phase angles. Data value indexes defined by power_phase_type. */
    public var avgLeftPowerPhase: List<Double?>?
        get() {
            val field = getField(AVG_LEFT_POWER_PHASE_FIELD_NUM) ?: return null
            return List(field.numValues) { getAvgLeftPowerPhase(it) }
        }
        set(value) {
            removeField(AVG_LEFT_POWER_PHASE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setAvgLeftPowerPhase(index, element) }
        }

    public fun getAvgLeftPowerPhase(index: Int): Double? =
        getFieldValue(AVG_LEFT_POWER_PHASE_FIELD_NUM, index) as? Double

    public fun setAvgLeftPowerPhase(index: Int, value: Double?) {
        setFieldValue(AVG_LEFT_POWER_PHASE_FIELD_NUM, value, index)
    }

    /** Number of values stored in avg_left_power_phase_peak. */
    public val numAvgLeftPowerPhasePeak: Int
        get() = getField(AVG_LEFT_POWER_PHASE_PEAK_FIELD_NUM)?.numValues ?: 0

    /** avg_left_power_phase_peak, in degrees. Average left power phase peak angles. Data value indexes defined by power_phase_type. */
    public var avgLeftPowerPhasePeak: List<Double?>?
        get() {
            val field = getField(AVG_LEFT_POWER_PHASE_PEAK_FIELD_NUM) ?: return null
            return List(field.numValues) { getAvgLeftPowerPhasePeak(it) }
        }
        set(value) {
            removeField(AVG_LEFT_POWER_PHASE_PEAK_FIELD_NUM)
            value?.forEachIndexed { index, element -> setAvgLeftPowerPhasePeak(index, element) }
        }

    public fun getAvgLeftPowerPhasePeak(index: Int): Double? =
        getFieldValue(AVG_LEFT_POWER_PHASE_PEAK_FIELD_NUM, index) as? Double

    public fun setAvgLeftPowerPhasePeak(index: Int, value: Double?) {
        setFieldValue(AVG_LEFT_POWER_PHASE_PEAK_FIELD_NUM, value, index)
    }

    /** Number of values stored in avg_right_power_phase. */
    public val numAvgRightPowerPhase: Int
        get() = getField(AVG_RIGHT_POWER_PHASE_FIELD_NUM)?.numValues ?: 0

    /** avg_right_power_phase, in degrees. Average right power phase angles. Data value indexes defined by power_phase_type. */
    public var avgRightPowerPhase: List<Double?>?
        get() {
            val field = getField(AVG_RIGHT_POWER_PHASE_FIELD_NUM) ?: return null
            return List(field.numValues) { getAvgRightPowerPhase(it) }
        }
        set(value) {
            removeField(AVG_RIGHT_POWER_PHASE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setAvgRightPowerPhase(index, element) }
        }

    public fun getAvgRightPowerPhase(index: Int): Double? =
        getFieldValue(AVG_RIGHT_POWER_PHASE_FIELD_NUM, index) as? Double

    public fun setAvgRightPowerPhase(index: Int, value: Double?) {
        setFieldValue(AVG_RIGHT_POWER_PHASE_FIELD_NUM, value, index)
    }

    /** Number of values stored in avg_right_power_phase_peak. */
    public val numAvgRightPowerPhasePeak: Int
        get() = getField(AVG_RIGHT_POWER_PHASE_PEAK_FIELD_NUM)?.numValues ?: 0

    /** avg_right_power_phase_peak, in degrees. Average right power phase peak angles. Data value indexes defined by power_phase_type. */
    public var avgRightPowerPhasePeak: List<Double?>?
        get() {
            val field = getField(AVG_RIGHT_POWER_PHASE_PEAK_FIELD_NUM) ?: return null
            return List(field.numValues) { getAvgRightPowerPhasePeak(it) }
        }
        set(value) {
            removeField(AVG_RIGHT_POWER_PHASE_PEAK_FIELD_NUM)
            value?.forEachIndexed { index, element -> setAvgRightPowerPhasePeak(index, element) }
        }

    public fun getAvgRightPowerPhasePeak(index: Int): Double? =
        getFieldValue(AVG_RIGHT_POWER_PHASE_PEAK_FIELD_NUM, index) as? Double

    public fun setAvgRightPowerPhasePeak(index: Int, value: Double?) {
        setFieldValue(AVG_RIGHT_POWER_PHASE_PEAK_FIELD_NUM, value, index)
    }

    /** Number of values stored in avg_power_position. */
    public val numAvgPowerPosition: Int
        get() = getField(AVG_POWER_POSITION_FIELD_NUM)?.numValues ?: 0

    /** avg_power_position, in watts. Average power by position. Data value indexes defined by rider_position_type. */
    public var avgPowerPosition: List<UShort?>?
        get() {
            val field = getField(AVG_POWER_POSITION_FIELD_NUM) ?: return null
            return List(field.numValues) { getAvgPowerPosition(it) }
        }
        set(value) {
            removeField(AVG_POWER_POSITION_FIELD_NUM)
            value?.forEachIndexed { index, element -> setAvgPowerPosition(index, element) }
        }

    public fun getAvgPowerPosition(index: Int): UShort? =
        getFieldValue(AVG_POWER_POSITION_FIELD_NUM, index) as? UShort

    public fun setAvgPowerPosition(index: Int, value: UShort?) {
        setFieldValue(AVG_POWER_POSITION_FIELD_NUM, value, index)
    }

    /** Number of values stored in max_power_position. */
    public val numMaxPowerPosition: Int
        get() = getField(MAX_POWER_POSITION_FIELD_NUM)?.numValues ?: 0

    /** max_power_position, in watts. Maximum power by position. Data value indexes defined by rider_position_type. */
    public var maxPowerPosition: List<UShort?>?
        get() {
            val field = getField(MAX_POWER_POSITION_FIELD_NUM) ?: return null
            return List(field.numValues) { getMaxPowerPosition(it) }
        }
        set(value) {
            removeField(MAX_POWER_POSITION_FIELD_NUM)
            value?.forEachIndexed { index, element -> setMaxPowerPosition(index, element) }
        }

    public fun getMaxPowerPosition(index: Int): UShort? =
        getFieldValue(MAX_POWER_POSITION_FIELD_NUM, index) as? UShort

    public fun setMaxPowerPosition(index: Int, value: UShort?) {
        setFieldValue(MAX_POWER_POSITION_FIELD_NUM, value, index)
    }

    /** Number of values stored in avg_cadence_position. */
    public val numAvgCadencePosition: Int
        get() = getField(AVG_CADENCE_POSITION_FIELD_NUM)?.numValues ?: 0

    /** avg_cadence_position, in rpm. Average cadence by position. Data value indexes defined by rider_position_type. */
    public var avgCadencePosition: List<UByte?>?
        get() {
            val field = getField(AVG_CADENCE_POSITION_FIELD_NUM) ?: return null
            return List(field.numValues) { getAvgCadencePosition(it) }
        }
        set(value) {
            removeField(AVG_CADENCE_POSITION_FIELD_NUM)
            value?.forEachIndexed { index, element -> setAvgCadencePosition(index, element) }
        }

    public fun getAvgCadencePosition(index: Int): UByte? =
        getFieldValue(AVG_CADENCE_POSITION_FIELD_NUM, index) as? UByte

    public fun setAvgCadencePosition(index: Int, value: UByte?) {
        setFieldValue(AVG_CADENCE_POSITION_FIELD_NUM, value, index)
    }

    /** Number of values stored in max_cadence_position. */
    public val numMaxCadencePosition: Int
        get() = getField(MAX_CADENCE_POSITION_FIELD_NUM)?.numValues ?: 0

    /** max_cadence_position, in rpm. Maximum cadence by position. Data value indexes defined by rider_position_type. */
    public var maxCadencePosition: List<UByte?>?
        get() {
            val field = getField(MAX_CADENCE_POSITION_FIELD_NUM) ?: return null
            return List(field.numValues) { getMaxCadencePosition(it) }
        }
        set(value) {
            removeField(MAX_CADENCE_POSITION_FIELD_NUM)
            value?.forEachIndexed { index, element -> setMaxCadencePosition(index, element) }
        }

    public fun getMaxCadencePosition(index: Int): UByte? =
        getFieldValue(MAX_CADENCE_POSITION_FIELD_NUM, index) as? UByte

    public fun setMaxCadencePosition(index: Int, value: UByte?) {
        setFieldValue(MAX_CADENCE_POSITION_FIELD_NUM, value, index)
    }

    /** enhanced_avg_speed, in m/s. */
    public var enhancedAvgSpeed: Double?
        get() = getFieldValue(ENHANCED_AVG_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(ENHANCED_AVG_SPEED_FIELD_NUM, value)

    /** enhanced_max_speed, in m/s. */
    public var enhancedMaxSpeed: Double?
        get() = getFieldValue(ENHANCED_MAX_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(ENHANCED_MAX_SPEED_FIELD_NUM, value)

    /** enhanced_avg_altitude, in m. */
    public var enhancedAvgAltitude: Double?
        get() = getFieldValue(ENHANCED_AVG_ALTITUDE_FIELD_NUM) as? Double
        set(value) = setFieldValue(ENHANCED_AVG_ALTITUDE_FIELD_NUM, value)

    /** enhanced_min_altitude, in m. */
    public var enhancedMinAltitude: Double?
        get() = getFieldValue(ENHANCED_MIN_ALTITUDE_FIELD_NUM) as? Double
        set(value) = setFieldValue(ENHANCED_MIN_ALTITUDE_FIELD_NUM, value)

    /** enhanced_max_altitude, in m. */
    public var enhancedMaxAltitude: Double?
        get() = getFieldValue(ENHANCED_MAX_ALTITUDE_FIELD_NUM) as? Double
        set(value) = setFieldValue(ENHANCED_MAX_ALTITUDE_FIELD_NUM, value)

    /** avg_lev_motor_power, in watts. lev average motor power during lap */
    public var avgLevMotorPower: UShort?
        get() = getFieldValue(AVG_LEV_MOTOR_POWER_FIELD_NUM) as? UShort
        set(value) = setFieldValue(AVG_LEV_MOTOR_POWER_FIELD_NUM, value)

    /** max_lev_motor_power, in watts. lev maximum motor power during lap */
    public var maxLevMotorPower: UShort?
        get() = getFieldValue(MAX_LEV_MOTOR_POWER_FIELD_NUM) as? UShort
        set(value) = setFieldValue(MAX_LEV_MOTOR_POWER_FIELD_NUM, value)

    /** lev_battery_consumption, in percent. lev battery consumption during lap */
    public var levBatteryConsumption: Double?
        get() = getFieldValue(LEV_BATTERY_CONSUMPTION_FIELD_NUM) as? Double
        set(value) = setFieldValue(LEV_BATTERY_CONSUMPTION_FIELD_NUM, value)

    /** avg_vertical_ratio, in percent. */
    public var avgVerticalRatio: Double?
        get() = getFieldValue(AVG_VERTICAL_RATIO_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_VERTICAL_RATIO_FIELD_NUM, value)

    /** avg_stance_time_balance, in percent. */
    public var avgStanceTimeBalance: Double?
        get() = getFieldValue(AVG_STANCE_TIME_BALANCE_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_STANCE_TIME_BALANCE_FIELD_NUM, value)

    /** avg_step_length, in mm. */
    public var avgStepLength: Double?
        get() = getFieldValue(AVG_STEP_LENGTH_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_STEP_LENGTH_FIELD_NUM, value)

    /** avg_vam, in m/s. */
    public var avgVam: Double?
        get() = getFieldValue(AVG_VAM_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_VAM_FIELD_NUM, value)

    /** avg_depth, in m. 0 if above water */
    public var avgDepth: Double?
        get() = getFieldValue(AVG_DEPTH_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_DEPTH_FIELD_NUM, value)

    /** max_depth, in m. 0 if above water */
    public var maxDepth: Double?
        get() = getFieldValue(MAX_DEPTH_FIELD_NUM) as? Double
        set(value) = setFieldValue(MAX_DEPTH_FIELD_NUM, value)

    /** min_temperature, in C. */
    public var minTemperature: Byte?
        get() = getFieldValue(MIN_TEMPERATURE_FIELD_NUM) as? Byte
        set(value) = setFieldValue(MIN_TEMPERATURE_FIELD_NUM, value)

    /** enhanced_avg_respiration_rate, in Breaths/min. */
    public var enhancedAvgRespirationRate: Double?
        get() = getFieldValue(ENHANCED_AVG_RESPIRATION_RATE_FIELD_NUM) as? Double
        set(value) = setFieldValue(ENHANCED_AVG_RESPIRATION_RATE_FIELD_NUM, value)

    /** enhanced_max_respiration_rate, in Breaths/min. */
    public var enhancedMaxRespirationRate: Double?
        get() = getFieldValue(ENHANCED_MAX_RESPIRATION_RATE_FIELD_NUM) as? Double
        set(value) = setFieldValue(ENHANCED_MAX_RESPIRATION_RATE_FIELD_NUM, value)

    /** avg_respiration_rate. */
    public var avgRespirationRate: UByte?
        get() = getFieldValue(AVG_RESPIRATION_RATE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(AVG_RESPIRATION_RATE_FIELD_NUM, value)

    /** max_respiration_rate. */
    public var maxRespirationRate: UByte?
        get() = getFieldValue(MAX_RESPIRATION_RATE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(MAX_RESPIRATION_RATE_FIELD_NUM, value)

    /** total_grit, in kGrit. The grit score estimates how challenging a route could be for a cyclist in terms of time spent going over sharp turns or large grade slopes. */
    public var totalGrit: Float?
        get() = getFieldValue(TOTAL_GRIT_FIELD_NUM) as? Float
        set(value) = setFieldValue(TOTAL_GRIT_FIELD_NUM, value)

    /** total_flow, in Flow. The flow score estimates how long distance wise a cyclist deaccelerates over intervals where deacceleration is unnecessary such as smooth turns or small grade angle intervals. */
    public var totalFlow: Float?
        get() = getFieldValue(TOTAL_FLOW_FIELD_NUM) as? Float
        set(value) = setFieldValue(TOTAL_FLOW_FIELD_NUM, value)

    /** jump_count. */
    public var jumpCount: UShort?
        get() = getFieldValue(JUMP_COUNT_FIELD_NUM) as? UShort
        set(value) = setFieldValue(JUMP_COUNT_FIELD_NUM, value)

    /** avg_grit, in kGrit. The grit score estimates how challenging a route could be for a cyclist in terms of time spent going over sharp turns or large grade slopes. */
    public var avgGrit: Float?
        get() = getFieldValue(AVG_GRIT_FIELD_NUM) as? Float
        set(value) = setFieldValue(AVG_GRIT_FIELD_NUM, value)

    /** avg_flow, in Flow. The flow score estimates how long distance wise a cyclist deaccelerates over intervals where deacceleration is unnecessary such as smooth turns or small grade angle intervals. */
    public var avgFlow: Float?
        get() = getFieldValue(AVG_FLOW_FIELD_NUM) as? Float
        set(value) = setFieldValue(AVG_FLOW_FIELD_NUM, value)

    /** metabolic_calories, in kcal. */
    public var metabolicCalories: UShort?
        get() = getFieldValue(METABOLIC_CALORIES_FIELD_NUM) as? UShort
        set(value) = setFieldValue(METABOLIC_CALORIES_FIELD_NUM, value)

    /** total_fractional_ascent, in m. fractional part of total_ascent */
    public var totalFractionalAscent: Double?
        get() = getFieldValue(TOTAL_FRACTIONAL_ASCENT_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_FRACTIONAL_ASCENT_FIELD_NUM, value)

    /** total_fractional_descent, in m. fractional part of total_descent */
    public var totalFractionalDescent: Double?
        get() = getFieldValue(TOTAL_FRACTIONAL_DESCENT_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_FRACTIONAL_DESCENT_FIELD_NUM, value)

    /** avg_core_temperature, in C. */
    public var avgCoreTemperature: Double?
        get() = getFieldValue(AVG_CORE_TEMPERATURE_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_CORE_TEMPERATURE_FIELD_NUM, value)

    /** min_core_temperature, in C. */
    public var minCoreTemperature: Double?
        get() = getFieldValue(MIN_CORE_TEMPERATURE_FIELD_NUM) as? Double
        set(value) = setFieldValue(MIN_CORE_TEMPERATURE_FIELD_NUM, value)

    /** max_core_temperature, in C. */
    public var maxCoreTemperature: Double?
        get() = getFieldValue(MAX_CORE_TEMPERATURE_FIELD_NUM) as? Double
        set(value) = setFieldValue(MAX_CORE_TEMPERATURE_FIELD_NUM, value)

    /** avg_grade_adjusted_speed, in m/s. */
    public var avgGradeAdjustedSpeed: Double?
        get() = getFieldValue(AVG_GRADE_ADJUSTED_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_GRADE_ADJUSTED_SPEED_FIELD_NUM, value)

    public companion object {
        public const val MESSAGE_INDEX_FIELD_NUM: UByte = 254u
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val EVENT_FIELD_NUM: UByte = 0u
        public const val EVENT_TYPE_FIELD_NUM: UByte = 1u
        public const val START_TIME_FIELD_NUM: UByte = 2u
        public const val START_POSITION_LAT_FIELD_NUM: UByte = 3u
        public const val START_POSITION_LONG_FIELD_NUM: UByte = 4u
        public const val END_POSITION_LAT_FIELD_NUM: UByte = 5u
        public const val END_POSITION_LONG_FIELD_NUM: UByte = 6u
        public const val TOTAL_ELAPSED_TIME_FIELD_NUM: UByte = 7u
        public const val TOTAL_TIMER_TIME_FIELD_NUM: UByte = 8u
        public const val TOTAL_DISTANCE_FIELD_NUM: UByte = 9u
        public const val TOTAL_CYCLES_FIELD_NUM: UByte = 10u
        public const val TOTAL_CALORIES_FIELD_NUM: UByte = 11u
        public const val TOTAL_FAT_CALORIES_FIELD_NUM: UByte = 12u
        public const val AVG_SPEED_FIELD_NUM: UByte = 13u
        public const val MAX_SPEED_FIELD_NUM: UByte = 14u
        public const val AVG_HEART_RATE_FIELD_NUM: UByte = 15u
        public const val MAX_HEART_RATE_FIELD_NUM: UByte = 16u
        public const val AVG_CADENCE_FIELD_NUM: UByte = 17u
        public const val MAX_CADENCE_FIELD_NUM: UByte = 18u
        public const val AVG_POWER_FIELD_NUM: UByte = 19u
        public const val MAX_POWER_FIELD_NUM: UByte = 20u
        public const val TOTAL_ASCENT_FIELD_NUM: UByte = 21u
        public const val TOTAL_DESCENT_FIELD_NUM: UByte = 22u
        public const val INTENSITY_FIELD_NUM: UByte = 23u
        public const val LAP_TRIGGER_FIELD_NUM: UByte = 24u
        public const val SPORT_FIELD_NUM: UByte = 25u
        public const val EVENT_GROUP_FIELD_NUM: UByte = 26u
        public const val NEC_LAT_FIELD_NUM: UByte = 27u
        public const val NEC_LONG_FIELD_NUM: UByte = 28u
        public const val SWC_LAT_FIELD_NUM: UByte = 29u
        public const val SWC_LONG_FIELD_NUM: UByte = 30u
        public const val NUM_LENGTHS_FIELD_NUM: UByte = 32u
        public const val NORMALIZED_POWER_FIELD_NUM: UByte = 33u
        public const val LEFT_RIGHT_BALANCE_FIELD_NUM: UByte = 34u
        public const val FIRST_LENGTH_INDEX_FIELD_NUM: UByte = 35u
        public const val AVG_STROKE_DISTANCE_FIELD_NUM: UByte = 37u
        public const val SWIM_STROKE_FIELD_NUM: UByte = 38u
        public const val SUB_SPORT_FIELD_NUM: UByte = 39u
        public const val NUM_ACTIVE_LENGTHS_FIELD_NUM: UByte = 40u
        public const val TOTAL_WORK_FIELD_NUM: UByte = 41u
        public const val AVG_ALTITUDE_FIELD_NUM: UByte = 42u
        public const val MAX_ALTITUDE_FIELD_NUM: UByte = 43u
        public const val GPS_ACCURACY_FIELD_NUM: UByte = 44u
        public const val AVG_GRADE_FIELD_NUM: UByte = 45u
        public const val AVG_POS_GRADE_FIELD_NUM: UByte = 46u
        public const val AVG_NEG_GRADE_FIELD_NUM: UByte = 47u
        public const val MAX_POS_GRADE_FIELD_NUM: UByte = 48u
        public const val MAX_NEG_GRADE_FIELD_NUM: UByte = 49u
        public const val AVG_TEMPERATURE_FIELD_NUM: UByte = 50u
        public const val MAX_TEMPERATURE_FIELD_NUM: UByte = 51u
        public const val TOTAL_MOVING_TIME_FIELD_NUM: UByte = 52u
        public const val AVG_POS_VERTICAL_SPEED_FIELD_NUM: UByte = 53u
        public const val AVG_NEG_VERTICAL_SPEED_FIELD_NUM: UByte = 54u
        public const val MAX_POS_VERTICAL_SPEED_FIELD_NUM: UByte = 55u
        public const val MAX_NEG_VERTICAL_SPEED_FIELD_NUM: UByte = 56u
        public const val TIME_IN_HR_ZONE_FIELD_NUM: UByte = 57u
        public const val TIME_IN_SPEED_ZONE_FIELD_NUM: UByte = 58u
        public const val TIME_IN_CADENCE_ZONE_FIELD_NUM: UByte = 59u
        public const val TIME_IN_POWER_ZONE_FIELD_NUM: UByte = 60u
        public const val REPETITION_NUM_FIELD_NUM: UByte = 61u
        public const val MIN_ALTITUDE_FIELD_NUM: UByte = 62u
        public const val MIN_HEART_RATE_FIELD_NUM: UByte = 63u
        public const val ACTIVE_TIME_FIELD_NUM: UByte = 70u
        public const val WKT_STEP_INDEX_FIELD_NUM: UByte = 71u
        public const val AVG_SWOLF_FIELD_NUM: UByte = 73u
        public const val OPPONENT_SCORE_FIELD_NUM: UByte = 74u
        public const val STROKE_COUNT_FIELD_NUM: UByte = 75u
        public const val ZONE_COUNT_FIELD_NUM: UByte = 76u
        public const val AVG_VERTICAL_OSCILLATION_FIELD_NUM: UByte = 77u
        public const val AVG_STANCE_TIME_PERCENT_FIELD_NUM: UByte = 78u
        public const val AVG_STANCE_TIME_FIELD_NUM: UByte = 79u
        public const val AVG_FRACTIONAL_CADENCE_FIELD_NUM: UByte = 80u
        public const val MAX_FRACTIONAL_CADENCE_FIELD_NUM: UByte = 81u
        public const val TOTAL_FRACTIONAL_CYCLES_FIELD_NUM: UByte = 82u
        public const val PLAYER_SCORE_FIELD_NUM: UByte = 83u
        public const val AVG_TOTAL_HEMOGLOBIN_CONC_FIELD_NUM: UByte = 84u
        public const val MIN_TOTAL_HEMOGLOBIN_CONC_FIELD_NUM: UByte = 85u
        public const val MAX_TOTAL_HEMOGLOBIN_CONC_FIELD_NUM: UByte = 86u
        public const val AVG_SATURATED_HEMOGLOBIN_PERCENT_FIELD_NUM: UByte = 87u
        public const val MIN_SATURATED_HEMOGLOBIN_PERCENT_FIELD_NUM: UByte = 88u
        public const val MAX_SATURATED_HEMOGLOBIN_PERCENT_FIELD_NUM: UByte = 89u
        public const val AVG_STROKES_PER_LENGTH_FIELD_NUM: UByte = 90u
        public const val AVG_LEFT_TORQUE_EFFECTIVENESS_FIELD_NUM: UByte = 91u
        public const val AVG_RIGHT_TORQUE_EFFECTIVENESS_FIELD_NUM: UByte = 92u
        public const val AVG_LEFT_PEDAL_SMOOTHNESS_FIELD_NUM: UByte = 93u
        public const val AVG_RIGHT_PEDAL_SMOOTHNESS_FIELD_NUM: UByte = 94u
        public const val AVG_COMBINED_PEDAL_SMOOTHNESS_FIELD_NUM: UByte = 95u
        public const val FRONT_GEAR_SHIFT_COUNT_FIELD_NUM: UByte = 96u
        public const val REAR_GEAR_SHIFT_COUNT_FIELD_NUM: UByte = 97u
        public const val TIME_STANDING_FIELD_NUM: UByte = 98u
        public const val STAND_COUNT_FIELD_NUM: UByte = 99u
        public const val AVG_LEFT_PCO_FIELD_NUM: UByte = 100u
        public const val AVG_RIGHT_PCO_FIELD_NUM: UByte = 101u
        public const val AVG_LEFT_POWER_PHASE_FIELD_NUM: UByte = 102u
        public const val AVG_LEFT_POWER_PHASE_PEAK_FIELD_NUM: UByte = 103u
        public const val AVG_RIGHT_POWER_PHASE_FIELD_NUM: UByte = 104u
        public const val AVG_RIGHT_POWER_PHASE_PEAK_FIELD_NUM: UByte = 105u
        public const val AVG_POWER_POSITION_FIELD_NUM: UByte = 106u
        public const val MAX_POWER_POSITION_FIELD_NUM: UByte = 107u
        public const val AVG_CADENCE_POSITION_FIELD_NUM: UByte = 108u
        public const val MAX_CADENCE_POSITION_FIELD_NUM: UByte = 109u
        public const val ENHANCED_AVG_SPEED_FIELD_NUM: UByte = 110u
        public const val ENHANCED_MAX_SPEED_FIELD_NUM: UByte = 111u
        public const val ENHANCED_AVG_ALTITUDE_FIELD_NUM: UByte = 112u
        public const val ENHANCED_MIN_ALTITUDE_FIELD_NUM: UByte = 113u
        public const val ENHANCED_MAX_ALTITUDE_FIELD_NUM: UByte = 114u
        public const val AVG_LEV_MOTOR_POWER_FIELD_NUM: UByte = 115u
        public const val MAX_LEV_MOTOR_POWER_FIELD_NUM: UByte = 116u
        public const val LEV_BATTERY_CONSUMPTION_FIELD_NUM: UByte = 117u
        public const val AVG_VERTICAL_RATIO_FIELD_NUM: UByte = 118u
        public const val AVG_STANCE_TIME_BALANCE_FIELD_NUM: UByte = 119u
        public const val AVG_STEP_LENGTH_FIELD_NUM: UByte = 120u
        public const val AVG_VAM_FIELD_NUM: UByte = 121u
        public const val AVG_DEPTH_FIELD_NUM: UByte = 122u
        public const val MAX_DEPTH_FIELD_NUM: UByte = 123u
        public const val MIN_TEMPERATURE_FIELD_NUM: UByte = 124u
        public const val ENHANCED_AVG_RESPIRATION_RATE_FIELD_NUM: UByte = 136u
        public const val ENHANCED_MAX_RESPIRATION_RATE_FIELD_NUM: UByte = 137u
        public const val AVG_RESPIRATION_RATE_FIELD_NUM: UByte = 147u
        public const val MAX_RESPIRATION_RATE_FIELD_NUM: UByte = 148u
        public const val TOTAL_GRIT_FIELD_NUM: UByte = 149u
        public const val TOTAL_FLOW_FIELD_NUM: UByte = 150u
        public const val JUMP_COUNT_FIELD_NUM: UByte = 151u
        public const val AVG_GRIT_FIELD_NUM: UByte = 153u
        public const val AVG_FLOW_FIELD_NUM: UByte = 154u
        public const val METABOLIC_CALORIES_FIELD_NUM: UByte = 155u
        public const val TOTAL_FRACTIONAL_ASCENT_FIELD_NUM: UByte = 156u
        public const val TOTAL_FRACTIONAL_DESCENT_FIELD_NUM: UByte = 157u
        public const val AVG_CORE_TEMPERATURE_FIELD_NUM: UByte = 158u
        public const val MIN_CORE_TEMPERATURE_FIELD_NUM: UByte = 159u
        public const val MAX_CORE_TEMPERATURE_FIELD_NUM: UByte = 160u
        public const val AVG_GRADE_ADJUSTED_SPEED_FIELD_NUM: UByte = 161u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[254] = Field("MessageIndex", 254u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[0] = Field("Event", 0u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[1] = Field("EventType", 1u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[2] = Field("StartTime", 2u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[3] = Field("StartPositionLat", 3u, BaseType.SINT32, 1.0, 0.0, "semicircles", false)
            fields[4] = Field("StartPositionLong", 4u, BaseType.SINT32, 1.0, 0.0, "semicircles", false)
            fields[5] = Field("EndPositionLat", 5u, BaseType.SINT32, 1.0, 0.0, "semicircles", false)
            fields[6] = Field("EndPositionLong", 6u, BaseType.SINT32, 1.0, 0.0, "semicircles", false)
            fields[7] = Field("TotalElapsedTime", 7u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[8] = Field("TotalTimerTime", 8u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[9] = Field("TotalDistance", 9u, BaseType.UINT32, 100.0, 0.0, "m", false)
            fields[10] = Field("TotalCycles", 10u, BaseType.UINT32, 1.0, 0.0, "cycles", false)
                .addSubField(
                    SubField("TotalStrides", BaseType.UINT32, 1.0, 0.0, "strides").also { subField ->
                        subField.addMap(25u, 1)
                        subField.addMap(25u, 11)
                    },
                )
                .addSubField(
                    SubField("TotalStrokes", BaseType.UINT32, 1.0, 0.0, "strokes").also { subField ->
                        subField.addMap(25u, 2)
                        subField.addMap(25u, 5)
                        subField.addMap(25u, 15)
                        subField.addMap(25u, 37)
                    },
                )
                .addSubField(
                    SubField("TotalPushes", BaseType.UINT32, 1.0, 0.0, "pushes").also { subField ->
                        subField.addMap(25u, 66)
                        subField.addMap(25u, 65)
                    },
                )
            fields[11] = Field("TotalCalories", 11u, BaseType.UINT16, 1.0, 0.0, "kcal", false)
            fields[12] = Field("TotalFatCalories", 12u, BaseType.UINT16, 1.0, 0.0, "kcal", false)
            fields[13] = Field("AvgSpeed", 13u, BaseType.UINT16, 1000.0, 0.0, "m/s", false)
                .addComponent(FieldComponent(110u, false, 16, 1000.0, 0.0))
            fields[14] = Field("MaxSpeed", 14u, BaseType.UINT16, 1000.0, 0.0, "m/s", false)
                .addComponent(FieldComponent(111u, false, 16, 1000.0, 0.0))
            fields[15] = Field("AvgHeartRate", 15u, BaseType.UINT8, 1.0, 0.0, "bpm", false)
            fields[16] = Field("MaxHeartRate", 16u, BaseType.UINT8, 1.0, 0.0, "bpm", false)
            fields[17] = Field("AvgCadence", 17u, BaseType.UINT8, 1.0, 0.0, "rpm", false)
                .addSubField(
                    SubField("AvgRunningCadence", BaseType.UINT8, 1.0, 0.0, "strides/min").also { subField ->
                        subField.addMap(25u, 1)
                    },
                )
                .addSubField(
                    SubField("AvgSwimmingCadence", BaseType.UINT8, 1.0, 0.0, "strokes/min").also { subField ->
                        subField.addMap(25u, 5)
                    },
                )
                .addSubField(
                    SubField("AvgPaddlesportCadence", BaseType.UINT8, 1.0, 0.0, "strokes/min").also { subField ->
                        subField.addMap(25u, 15)
                        subField.addMap(25u, 37)
                    },
                )
                .addSubField(
                    SubField("AvgPushCadence", BaseType.UINT8, 1.0, 0.0, "pushes/min").also { subField ->
                        subField.addMap(25u, 66)
                        subField.addMap(25u, 65)
                    },
                )
            fields[18] = Field("MaxCadence", 18u, BaseType.UINT8, 1.0, 0.0, "rpm", false)
                .addSubField(
                    SubField("MaxRunningCadence", BaseType.UINT8, 1.0, 0.0, "strides/min").also { subField ->
                        subField.addMap(25u, 1)
                    },
                )
                .addSubField(
                    SubField("MaxSwimmingCadence", BaseType.UINT8, 1.0, 0.0, "strokes/min").also { subField ->
                        subField.addMap(25u, 5)
                    },
                )
                .addSubField(
                    SubField("MaxPaddlesportCadence", BaseType.UINT8, 1.0, 0.0, "strokes/min").also { subField ->
                        subField.addMap(25u, 15)
                        subField.addMap(25u, 37)
                    },
                )
                .addSubField(
                    SubField("MaxPushCadence", BaseType.UINT8, 1.0, 0.0, "pushes/min").also { subField ->
                        subField.addMap(25u, 66)
                        subField.addMap(25u, 65)
                    },
                )
            fields[19] = Field("AvgPower", 19u, BaseType.UINT16, 1.0, 0.0, "watts", false)
            fields[20] = Field("MaxPower", 20u, BaseType.UINT16, 1.0, 0.0, "watts", false)
            fields[21] = Field("TotalAscent", 21u, BaseType.UINT16, 1.0, 0.0, "m", false)
            fields[22] = Field("TotalDescent", 22u, BaseType.UINT16, 1.0, 0.0, "m", false)
            fields[23] = Field("Intensity", 23u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[24] = Field("LapTrigger", 24u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[25] = Field("Sport", 25u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[26] = Field("EventGroup", 26u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[27] = Field("NecLat", 27u, BaseType.SINT32, 1.0, 0.0, "semicircles", false)
            fields[28] = Field("NecLong", 28u, BaseType.SINT32, 1.0, 0.0, "semicircles", false)
            fields[29] = Field("SwcLat", 29u, BaseType.SINT32, 1.0, 0.0, "semicircles", false)
            fields[30] = Field("SwcLong", 30u, BaseType.SINT32, 1.0, 0.0, "semicircles", false)
            fields[32] = Field("NumLengths", 32u, BaseType.UINT16, 1.0, 0.0, "lengths", false)
            fields[33] = Field("NormalizedPower", 33u, BaseType.UINT16, 1.0, 0.0, "watts", false)
            fields[34] = Field("LeftRightBalance", 34u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[35] = Field("FirstLengthIndex", 35u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[37] = Field("AvgStrokeDistance", 37u, BaseType.UINT16, 100.0, 0.0, "m", false)
            fields[38] = Field("SwimStroke", 38u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[39] = Field("SubSport", 39u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[40] = Field("NumActiveLengths", 40u, BaseType.UINT16, 1.0, 0.0, "lengths", false)
            fields[41] = Field("TotalWork", 41u, BaseType.UINT32, 1.0, 0.0, "J", false)
            fields[42] = Field("AvgAltitude", 42u, BaseType.UINT16, 5.0, 500.0, "m", false)
                .addComponent(FieldComponent(112u, false, 16, 5.0, 500.0))
            fields[43] = Field("MaxAltitude", 43u, BaseType.UINT16, 5.0, 500.0, "m", false)
                .addComponent(FieldComponent(114u, false, 16, 5.0, 500.0))
            fields[44] = Field("GpsAccuracy", 44u, BaseType.UINT8, 1.0, 0.0, "m", false)
            fields[45] = Field("AvgGrade", 45u, BaseType.SINT16, 100.0, 0.0, "%", false)
            fields[46] = Field("AvgPosGrade", 46u, BaseType.SINT16, 100.0, 0.0, "%", false)
            fields[47] = Field("AvgNegGrade", 47u, BaseType.SINT16, 100.0, 0.0, "%", false)
            fields[48] = Field("MaxPosGrade", 48u, BaseType.SINT16, 100.0, 0.0, "%", false)
            fields[49] = Field("MaxNegGrade", 49u, BaseType.SINT16, 100.0, 0.0, "%", false)
            fields[50] = Field("AvgTemperature", 50u, BaseType.SINT8, 1.0, 0.0, "C", false)
            fields[51] = Field("MaxTemperature", 51u, BaseType.SINT8, 1.0, 0.0, "C", false)
            fields[52] = Field("TotalMovingTime", 52u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[53] = Field("AvgPosVerticalSpeed", 53u, BaseType.SINT16, 1000.0, 0.0, "m/s", false)
            fields[54] = Field("AvgNegVerticalSpeed", 54u, BaseType.SINT16, 1000.0, 0.0, "m/s", false)
            fields[55] = Field("MaxPosVerticalSpeed", 55u, BaseType.SINT16, 1000.0, 0.0, "m/s", false)
            fields[56] = Field("MaxNegVerticalSpeed", 56u, BaseType.SINT16, 1000.0, 0.0, "m/s", false)
            fields[57] = Field("TimeInHrZone", 57u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[58] = Field("TimeInSpeedZone", 58u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[59] = Field("TimeInCadenceZone", 59u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[60] = Field("TimeInPowerZone", 60u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[61] = Field("RepetitionNum", 61u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[62] = Field("MinAltitude", 62u, BaseType.UINT16, 5.0, 500.0, "m", false)
                .addComponent(FieldComponent(113u, false, 16, 5.0, 500.0))
            fields[63] = Field("MinHeartRate", 63u, BaseType.UINT8, 1.0, 0.0, "bpm", false)
            fields[70] = Field("ActiveTime", 70u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[71] = Field("WktStepIndex", 71u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[73] = Field("AvgSwolf", 73u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[74] = Field("OpponentScore", 74u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[75] = Field("StrokeCount", 75u, BaseType.UINT16, 1.0, 0.0, "counts", false)
            fields[76] = Field("ZoneCount", 76u, BaseType.UINT16, 1.0, 0.0, "counts", false)
            fields[77] = Field("AvgVerticalOscillation", 77u, BaseType.UINT16, 10.0, 0.0, "mm", false)
            fields[78] = Field("AvgStanceTimePercent", 78u, BaseType.UINT16, 100.0, 0.0, "percent", false)
            fields[79] = Field("AvgStanceTime", 79u, BaseType.UINT16, 10.0, 0.0, "ms", false)
            fields[80] = Field("AvgFractionalCadence", 80u, BaseType.UINT8, 128.0, 0.0, "rpm", false)
            fields[81] = Field("MaxFractionalCadence", 81u, BaseType.UINT8, 128.0, 0.0, "rpm", false)
            fields[82] = Field("TotalFractionalCycles", 82u, BaseType.UINT8, 128.0, 0.0, "cycles", false)
            fields[83] = Field("PlayerScore", 83u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[84] = Field("AvgTotalHemoglobinConc", 84u, BaseType.UINT16, 100.0, 0.0, "g/dL", false)
            fields[85] = Field("MinTotalHemoglobinConc", 85u, BaseType.UINT16, 100.0, 0.0, "g/dL", false)
            fields[86] = Field("MaxTotalHemoglobinConc", 86u, BaseType.UINT16, 100.0, 0.0, "g/dL", false)
            fields[87] = Field("AvgSaturatedHemoglobinPercent", 87u, BaseType.UINT16, 10.0, 0.0, "%", false)
            fields[88] = Field("MinSaturatedHemoglobinPercent", 88u, BaseType.UINT16, 10.0, 0.0, "%", false)
            fields[89] = Field("MaxSaturatedHemoglobinPercent", 89u, BaseType.UINT16, 10.0, 0.0, "%", false)
            fields[90] = Field("AvgStrokesPerLength", 90u, BaseType.UINT16, 10.0, 0.0, "strokes / length", false)
            fields[91] = Field("AvgLeftTorqueEffectiveness", 91u, BaseType.UINT8, 2.0, 0.0, "percent", false)
            fields[92] = Field("AvgRightTorqueEffectiveness", 92u, BaseType.UINT8, 2.0, 0.0, "percent", false)
            fields[93] = Field("AvgLeftPedalSmoothness", 93u, BaseType.UINT8, 2.0, 0.0, "percent", false)
            fields[94] = Field("AvgRightPedalSmoothness", 94u, BaseType.UINT8, 2.0, 0.0, "percent", false)
            fields[95] = Field("AvgCombinedPedalSmoothness", 95u, BaseType.UINT8, 2.0, 0.0, "percent", false)
            fields[96] = Field("FrontGearShiftCount", 96u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[97] = Field("RearGearShiftCount", 97u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[98] = Field("TimeStanding", 98u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[99] = Field("StandCount", 99u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[100] = Field("AvgLeftPco", 100u, BaseType.SINT8, 1.0, 0.0, "mm", false)
            fields[101] = Field("AvgRightPco", 101u, BaseType.SINT8, 1.0, 0.0, "mm", false)
            fields[102] = Field("AvgLeftPowerPhase", 102u, BaseType.UINT8, 0.7111111, 0.0, "degrees", false)
            fields[103] = Field("AvgLeftPowerPhasePeak", 103u, BaseType.UINT8, 0.7111111, 0.0, "degrees", false)
            fields[104] = Field("AvgRightPowerPhase", 104u, BaseType.UINT8, 0.7111111, 0.0, "degrees", false)
            fields[105] = Field("AvgRightPowerPhasePeak", 105u, BaseType.UINT8, 0.7111111, 0.0, "degrees", false)
            fields[106] = Field("AvgPowerPosition", 106u, BaseType.UINT16, 1.0, 0.0, "watts", false)
            fields[107] = Field("MaxPowerPosition", 107u, BaseType.UINT16, 1.0, 0.0, "watts", false)
            fields[108] = Field("AvgCadencePosition", 108u, BaseType.UINT8, 1.0, 0.0, "rpm", false)
            fields[109] = Field("MaxCadencePosition", 109u, BaseType.UINT8, 1.0, 0.0, "rpm", false)
            fields[110] = Field("EnhancedAvgSpeed", 110u, BaseType.UINT32, 1000.0, 0.0, "m/s", false)
            fields[111] = Field("EnhancedMaxSpeed", 111u, BaseType.UINT32, 1000.0, 0.0, "m/s", false)
            fields[112] = Field("EnhancedAvgAltitude", 112u, BaseType.UINT32, 5.0, 500.0, "m", false)
            fields[113] = Field("EnhancedMinAltitude", 113u, BaseType.UINT32, 5.0, 500.0, "m", false)
            fields[114] = Field("EnhancedMaxAltitude", 114u, BaseType.UINT32, 5.0, 500.0, "m", false)
            fields[115] = Field("AvgLevMotorPower", 115u, BaseType.UINT16, 1.0, 0.0, "watts", false)
            fields[116] = Field("MaxLevMotorPower", 116u, BaseType.UINT16, 1.0, 0.0, "watts", false)
            fields[117] = Field("LevBatteryConsumption", 117u, BaseType.UINT8, 2.0, 0.0, "percent", false)
            fields[118] = Field("AvgVerticalRatio", 118u, BaseType.UINT16, 100.0, 0.0, "percent", false)
            fields[119] = Field("AvgStanceTimeBalance", 119u, BaseType.UINT16, 100.0, 0.0, "percent", false)
            fields[120] = Field("AvgStepLength", 120u, BaseType.UINT16, 10.0, 0.0, "mm", false)
            fields[121] = Field("AvgVam", 121u, BaseType.UINT16, 1000.0, 0.0, "m/s", false)
            fields[122] = Field("AvgDepth", 122u, BaseType.UINT32, 1000.0, 0.0, "m", false)
            fields[123] = Field("MaxDepth", 123u, BaseType.UINT32, 1000.0, 0.0, "m", false)
            fields[124] = Field("MinTemperature", 124u, BaseType.SINT8, 1.0, 0.0, "C", false)
            fields[136] = Field("EnhancedAvgRespirationRate", 136u, BaseType.UINT16, 100.0, 0.0, "Breaths/min", false)
            fields[137] = Field("EnhancedMaxRespirationRate", 137u, BaseType.UINT16, 100.0, 0.0, "Breaths/min", false)
            fields[147] = Field("AvgRespirationRate", 147u, BaseType.UINT8, 1.0, 0.0, "", false)
                .addComponent(FieldComponent(136u, false, 8, 1.0, 0.0))
            fields[148] = Field("MaxRespirationRate", 148u, BaseType.UINT8, 1.0, 0.0, "", false)
                .addComponent(FieldComponent(137u, false, 8, 1.0, 0.0))
            fields[149] = Field("TotalGrit", 149u, BaseType.FLOAT32, 1.0, 0.0, "kGrit", false)
            fields[150] = Field("TotalFlow", 150u, BaseType.FLOAT32, 1.0, 0.0, "Flow", false)
            fields[151] = Field("JumpCount", 151u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[153] = Field("AvgGrit", 153u, BaseType.FLOAT32, 1.0, 0.0, "kGrit", false)
            fields[154] = Field("AvgFlow", 154u, BaseType.FLOAT32, 1.0, 0.0, "Flow", false)
            fields[155] = Field("MetabolicCalories", 155u, BaseType.UINT16, 1.0, 0.0, "kcal", false)
            fields[156] = Field("TotalFractionalAscent", 156u, BaseType.UINT8, 100.0, 0.0, "m", false)
            fields[157] = Field("TotalFractionalDescent", 157u, BaseType.UINT8, 100.0, 0.0, "m", false)
            fields[158] = Field("AvgCoreTemperature", 158u, BaseType.UINT16, 100.0, 0.0, "C", false)
            fields[159] = Field("MinCoreTemperature", 159u, BaseType.UINT16, 100.0, 0.0, "C", false)
            fields[160] = Field("MaxCoreTemperature", 160u, BaseType.UINT16, 100.0, 0.0, "C", false)
            fields[161] = Field("AvgGradeAdjustedSpeed", 161u, BaseType.UINT32, 1000.0, 0.0, "m/s", false)
            return fields
        }
    }
}
